/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect, useState } from "react";

import { postData } from "Utils/server";
import { getAuthorizationHeader } from "Utils/helpers";

import { useSoftUIController } from "context";

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// @mui material components

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";

// Wizard application components

const globalCss = require("assets/theme/base/globals");

// sweetalert2 components
import Swal from "sweetalert2";
import DataTable from "Tables/DataTable";

function SaveTemplate({
  provider,
  template,
  filterSportData,
  filterComptetitionData,
  handleStepperButtonStatus,
  handleStepData,
}) {
  const [controller] = useSoftUIController();
  const { providers } = controller;

  const [conflicts, setConflicts] = useState(null);
  const [providersData, setProvidersData] = useState(null);
  const [chekingForConflicts, setCheckForConflicts] = useState(null);

  const css = globalCss ? globalCss.default : null;
  const customColors = css.customColors;

  
  const [dataTableData, setDataTableData] = useState({
    columns: [
      { Header: "sport", accessor: "sport", width: "100px" },
      { Header: "region", accessor: "region", width: "120px" },
      { Header: "id", accessor: "id", width: "50px" },
      { Header: "name", accessor: "name", width: "120px" },
      {
        Header: "conflicts",
        accessor: "conflictString",
        width: "120px",
        Cell: renderConflicts,
      },
    ],

    rows: [],
  });

  function renderConflicts({ value, ...rest }) {
    let conflicts = value;
    conflicts = JSON.parse(conflicts);
    return conflicts ? (
      <div style={{ padding: "0px", width: "100%" }}>
        {conflicts.map((cl, index) => {
          return (
            <div
              key={cl.provider_id + "-" + index}
              style={{
                display: "flex",
                borderBottom: index == conflicts.length - 1 ? "" : "0.0625rem solid #e9ecef",
                backgroundColor: conflicts.length == 1 ? "" : index % 2 ? "" : "rgb(249, 249, 249)",
                padding: "5px 15px",
              }}
            >
              <SoftTypography
                sx={{
                  fontFamily:
                    "-apple-system,BlinkMacSystemFont,Helvetica Neue,Segoe UI,Tahoma,Roboto,Oxygen,Ubuntu,Cantarell,Fira Sans,Open Sans,sans-serif",
                  fontSize: "1rem",
                  fontWeight: "800",
                  lineHeight: "25px",
                  fontStyle: "italic",
                  opacity: 0.5,
                  color: providers
                    ? customColors[providers.find((p) => p.value == cl.provider_id)["label"]]
                    : "inherit",
                  maxWidth: "7em",
                  minWidth: "7em",
                }}
              >
                {providers ? providers.find((p) => p.value == cl.provider_id)["label"] : ""}
              </SoftTypography>
              <SoftTypography
                sx={{
                  paddingLeft: "10px",
                  paddingRight: "10px",
                  minWidth: "8em",
                  fontSize: "0.875rem",
                }}
              >
                {cl.region}
              </SoftTypography>
              <SoftTypography
                sx={{
                  paddingLeft: "10px",
                  paddingRight: "10px",
                  width: "100%",
                  fontSize: "0.875rem",
                }}
              >
                {cl.competition_id}
              </SoftTypography>
              <SoftTypography
                sx={{
                  paddingLeft: "10px",
                  paddingRight: "10px",
                  width: "100%",
                  fontSize: "0.875rem",
                }}
              >
                {cl.competition}
              </SoftTypography>
            </div>
          );
        })}
      </div>
    ) : (
      ""
    );
  }

  const checkForConflicts = async (provider, template, sports, competitions) => {
    handleStepperButtonStatus(true);
    setCheckForConflicts(true);

    let sportsTmp = JSON.stringify(sports);
    sportsTmp = JSON.parse(sportsTmp);

    let competitionsTmp = JSON.stringify(competitions);
    competitionsTmp = JSON.parse(competitionsTmp);

    Swal.fire({
      title: "We proccess your request! ",
      html: "Wait for few seconds...",
      timerProgressBar: true,
      didOpen: async () => {
        Swal.showLoading();
        const providerId = provider.id;
        const templateId = template;
        let objToSend = {
          sports: sportsTmp.map((sp) => {
            return {
              id: sp.id,
              default: sp.defaultvalue,
              inplay: sp.both ? sp.both : sp.inplay,
              pregame: sp.both ? sp.both : sp.pregame,
            };
          }),
          competitions: competitionsTmp
            .filter((item) => item.inherit == false)
            .map((cp) => {
              return {
                id: cp.id,
                sportId: sportsTmp.find((sp) => sp.name == cp.sport)["id"],
                inplay: cp.inplay == null ? false : cp.inplay,
                pregame: cp.pregame == null ? false : cp.pregame,
              };
            }),
        };

        let checkConflictsEndpoint = `/api/v1/templates/${templateId}/${providerId}/check-conflicts`;
        let response = await postData(checkConflictsEndpoint, objToSend, {
          Authorization: getAuthorizationHeader(),
        });

        if (!response) {
          Swal.clickDeny();
        } else {
          if (response.status === 200) {
            let conflicts = response.data;

            conflicts.map(
              (cl) =>
                (cl.conflictString = cl.conflictProviders
                  ? JSON.stringify(cl.conflictProviders)
                  : "[]")
            );

            conflicts.sort((a, b) => a.sport_id - b.sport_id);
            setConflicts(conflicts);

            dataTableData.rows = conflicts;

            setDataTableData(dataTableData);

            handleStepperButtonStatus(false);
            setCheckForConflicts(false);

            Swal.clickConfirm();
          }
        }
      },
      willClose: () => {},
      allowOutsideClick: false,
    });
  };

  useEffect(() => {
    checkForConflicts(provider, template, filterSportData, filterComptetitionData);
  }, [provider, template, filterComptetitionData, filterSportData]);

  useEffect(() => {
    if (providers && providers.length > 0) setProvidersData(providers);
  }, [providers]);

  return (
    <SoftBox>
      <SoftBox width="80%" textAlign="center" mx="auto" mb={4}>
        <SoftBox mb={1}>
          <SoftTypography variant="h5" fontWeight="regular">
            You are Ready to Save your new Filter
          </SoftTypography>
        </SoftBox>
        <SoftTypography variant="body2" fontWeight="regular" color="text">
          Remember, that you can customize, disable or delete a Filter that you have already create,
          when ever you want
        </SoftTypography>
      </SoftBox>

      {chekingForConflicts ? (
        <SoftTypography
          variant="body2"
          fontWeight="regular"
          color="text"
          style={{ textAlign: "center" }}
        >
          Checking for Conflicts....
        </SoftTypography>
      ) : (
        ""
      )}

      {conflicts && conflicts.length > 0 && !chekingForConflicts ? (
        <div>
          <SoftTypography
            variant="body2"
            color="primary"
            style={{ marginBottom: "10px", paddingBottom: "10px" }}
          >
            There are some Conflicts between the Competitions of this Filter you are going to Save
            and the other Filters you have allready apply to this Template. Please be ensure you
            have check them carefully.
          </SoftTypography>
        </div>
      ) : (
        " "
      )}

      {(!conflicts || conflicts.length == 0) && !chekingForConflicts ? (
        <div>
          <SoftTypography
            variant="body2"
            fontWeight="regular"
            color="success"
            style={{
              display:
                chekingForConflicts == true ? "none" : chekingForConflicts == false ? "" : "none",
            }}
          >
            Congratulations! No Competitions Conflicts found in the Template.
          </SoftTypography>
        </div>
      ) : (
        " "
      )}

      <div className="conflicts-holder">
        <DataTable
          table={dataTableData}
          entriesPerPage={{
            defaultValue: 10,
            entries: [5, 10, 15, 20, 25],
          }}
          headerClassName="conflicts"
          bodyClassName="conflicts"
          canSearch
        />
      </div>
    </SoftBox>
  );
}

// Typechecking props for the DataTable
SaveTemplate.propTypes = {
  provider: PropTypes.object,
  template: PropTypes.string,
  filterSportData: PropTypes.array,
  filterComptetitionData: PropTypes.array,
  handleStepperButtonStatus: PropTypes.func,
  handleStepData: PropTypes.func,
};

export default SaveTemplate;
